import {inject, injectable} from "inversify";
import {action, configure, makeObservable, observable} from "mobx";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {translate} from "@dropDesk/storage/i18n/translate_helper";
import {formatDateTimeForReportApi} from "@dropDesk/utils/helpers/date_helper";
import {
    GetReportTicketComparativeUseCase
} from "@dropDesk/domain/use_case/reports/report_ticket_comparative/get_report_ticket_comparative.usecase";
import {
    ExportHtmlReportTicketComparativeUseCase
} from "@dropDesk/domain/use_case/reports/report_ticket_comparative/export_html_report_ticket_comparative.usecase";
import {
    ReportTicketComparativeEntity
} from "@dropDesk/domain/entities/reports/report_ticket_comparative/report_ticket_comparative.entity";
import {
    ComparativeDateParams,
    ComparativePeriodRange,
    ComparativePresetEnum,
    getComparativeDateParams,
    getComparativePeriodFromPreset,
    setComparativeDateParams
} from "@dropDesk/utils/helpers/comparative_period.helper";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import {ReportEntityFilters} from "@dropDesk/presentation/components/filters/report_entity_filters";

configure({
    enforceActions: "always",
});

export type ComparativeDateErrors = {
    periodStart?: string;
    periodEnd?: string;
    compareStart?: string;
    compareEnd?: string;
};

@injectable()
export class ReportTicketComparativeController {
    private readonly _getReportTicketComparativeUseCase: GetReportTicketComparativeUseCase;
    private readonly _exportHtmlReportTicketComparativeUseCase: ExportHtmlReportTicketComparativeUseCase;

    constructor(
        @inject(GetReportTicketComparativeUseCase) getReportTicketComparativeUseCase: GetReportTicketComparativeUseCase,
        @inject(ExportHtmlReportTicketComparativeUseCase) exportHtmlReportTicketComparativeUseCase: ExportHtmlReportTicketComparativeUseCase,
    ) {
        makeObservable(this);
        this._getReportTicketComparativeUseCase = getReportTicketComparativeUseCase;
        this._exportHtmlReportTicketComparativeUseCase = exportHtmlReportTicketComparativeUseCase;
    }

    @observable
    filterClient: ClientEntity | null = null;

    @observable
    filterUserClient: UserEntity | null = null;

    @observable
    filterSector: SectorEntity | null = null;

    @observable
    filterAttendant: UserEntity | null = null;

    @observable
    filterDescriptionStatic: TicketsDescriptionStaticEntity | null = null;

    @observable
    period: ComparativePeriodRange = getComparativeDateParams().period;

    @observable
    comparePeriod: ComparativePeriodRange = getComparativeDateParams().comparePeriod;

    @observable
    preset: ComparativePresetEnum | null = getComparativeDateParams().preset;

    @observable
    report: ReportTicketComparativeEntity | undefined;

    @observable
    loading = false;

    @observable
    loadingMessage?: string | null = null;

    @observable
    dateErrors: ComparativeDateErrors = {};

    @action
    setFilterClient(client: ClientEntity | null) {
        this.filterClient = client;
        if (!client) {
            this.filterUserClient = null;
        }
        this.getComparativeReport();
    }

    @action
    setFilterUserClient(contact: UserEntity | null) {
        this.filterUserClient = contact;
        this.getComparativeReport();
    }

    @action
    setFilterSector(sector: SectorEntity | null) {
        this.filterSector = sector;
        this.getComparativeReport();
    }

    @action
    setFilterAttendant(attendant: UserEntity | null) {
        this.filterAttendant = attendant;
        this.getComparativeReport();
    }

    @action
    setFilterDescriptionStatic(descriptionStatic: TicketsDescriptionStaticEntity | null) {
        this.filterDescriptionStatic = descriptionStatic;
        this.getComparativeReport();
    }

    @action
    clearEntityFilters() {
        this.filterClient = null;
        this.filterUserClient = null;
        this.filterSector = null;
        this.filterAttendant = null;
        this.filterDescriptionStatic = null;
        this.getComparativeReport();
    }

    private buildEntityFilters(): ReportEntityFilters | undefined {
        const filters: ReportEntityFilters = {};
        if (this.filterClient) {
            filters.idClient = this.filterClient.id;
        }
        if (this.filterUserClient) {
            filters.idContact = this.filterUserClient.id;
        }
        if (this.filterSector) {
            filters.idSector = this.filterSector.id;
        }
        if (this.filterAttendant) {
            filters.idUser = this.filterAttendant.id;
        }
        if (this.filterDescriptionStatic) {
            filters.idDescriptionStatic = this.filterDescriptionStatic.id;
        }
        return Object.keys(filters).length > 0 ? filters : undefined;
    }

    @action
    setLoadingMessage(message?: string | null) {
        this.loadingMessage = message;
    }

    @action
    startLoading(loadingMessage?: string | null) {
        this.setLoadingMessage(loadingMessage ?? "report_ticket_comparative.listComparative");
        this.loading = true;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.setLoadingMessage(null);
    }

    @action
    setDateErrors(errors: ComparativeDateErrors) {
        this.dateErrors = errors;
    }

    @action
    clearDateErrors() {
        this.dateErrors = {};
    }

    @action
    private persistDateParams() {
        setComparativeDateParams({
            period: this.period,
            comparePeriod: this.comparePeriod,
            preset: this.preset,
        });
    }

    @action
    private setDateParams(params: ComparativeDateParams) {
        this.period = params.period;
        this.comparePeriod = params.comparePeriod;
        this.preset = params.preset;
        this.persistDateParams();
    }

    @action
    setPeriodStart(date: Date | null) {
        this.period = [date, this.period[1]];
        this.preset = null;
        this.persistDateParams();
        this.getComparativeReport();
    }

    @action
    setPeriodEnd(date: Date | null) {
        this.period = [this.period[0], date];
        this.preset = null;
        this.persistDateParams();
        this.getComparativeReport();
    }

    @action
    setComparePeriodStart(date: Date | null) {
        this.comparePeriod = [date, this.comparePeriod[1]];
        this.preset = null;
        this.persistDateParams();
        this.getComparativeReport();
    }

    @action
    setComparePeriodEnd(date: Date | null) {
        this.comparePeriod = [this.comparePeriod[0], date];
        this.preset = null;
        this.persistDateParams();
        this.getComparativeReport();
    }

    @action
    applyPreset(preset: ComparativePresetEnum) {
        const params = getComparativePeriodFromPreset(preset);
        this.setDateParams(params);
        this.getComparativeReport();
    }

    @action
    setReport(report: ReportTicketComparativeEntity) {
        this.report = report;
    }

    @action
    isValidPeriodRange(range: ComparativePeriodRange): boolean {
        if (!range[0] || !range[1]) {
            return false;
        }
        return range[0].getTime() < range[1].getTime();
    }

    @action
    private validatePeriods(): boolean {
        const errors: ComparativeDateErrors = {};
        const requiredMessage = translate("report_ticket_comparative.errors.required");
        const invalidRangeMessage = translate("report_ticket_comparative.errors.invalidRange");
        const compareMustBeBeforeMessage = translate("report_ticket_comparative.errors.compareMustBeBefore");

        if (!this.period[0]) {
            errors.periodStart = requiredMessage;
        }
        if (!this.period[1]) {
            errors.periodEnd = requiredMessage;
        }
        if (!this.comparePeriod[0]) {
            errors.compareStart = requiredMessage;
        }
        if (!this.comparePeriod[1]) {
            errors.compareEnd = requiredMessage;
        }

        if (this.period[0] && this.period[1] && !this.isValidPeriodRange(this.period)) {
            errors.periodEnd = invalidRangeMessage;
        }

        if (this.comparePeriod[0] && this.comparePeriod[1] && !this.isValidPeriodRange(this.comparePeriod)) {
            errors.compareEnd = invalidRangeMessage;
        }

        if (
            this.isValidPeriodRange(this.period) &&
            this.isValidPeriodRange(this.comparePeriod) &&
            this.comparePeriod[1]!.getTime() > this.period[0]!.getTime()
        ) {
            errors.compareEnd = compareMustBeBeforeMessage;
        }

        this.setDateErrors(errors);
        return Object.keys(errors).length === 0;
    }

    @action
    private mapServerErrorToFields(errorMessage: string) {
        if (errorMessage === "compare_period_must_be_before_period") {
            const message = translate("report_ticket_comparative.errors.compareMustBeBefore");
            this.setDateErrors({
                compareEnd: message,
            });
            return;
        }

        if (errorMessage === "invalid_range_date") {
            const message = translate("report_ticket_comparative.errors.invalidRange");
            this.setDateErrors({
                periodEnd: message,
                compareEnd: message,
            });
            return;
        }

        if (errorMessage === "invalid_date") {
            const message = translate("server_messages.invalid_date");
            this.setDateErrors({
                periodStart: message,
                periodEnd: message,
                compareStart: message,
                compareEnd: message,
            });
        }
    }

    @action
    initialize() {
        const params = getComparativeDateParams();
        this.period = params.period;
        this.comparePeriod = params.comparePeriod;
        this.preset = params.preset;
        this.getComparativeReport();
    }

    @action
    getComparativeReport = async (): Promise<void> => {
        try {
            if (!this.validatePeriods()) {
                return;
            }

            this.clearDateErrors();
            this.startLoading("report_ticket_comparative.listComparative");
            const response = await this._getReportTicketComparativeUseCase.call(
                {
                    startDate: formatDateTimeForReportApi(this.period[0]!),
                    endDate: formatDateTimeForReportApi(this.period[1]!),
                },
                {
                    compareStartDate: formatDateTimeForReportApi(this.comparePeriod[0]!),
                    compareEndDate: formatDateTimeForReportApi(this.comparePeriod[1]!),
                },
                this.buildEntityFilters()
            );
            this.setReport(response);
            this.clearDateErrors();
            this.stopLoading();
        } catch (err: any) {
            const errorMessage = err.message ?? "unknown";
            this.mapServerErrorToFields(errorMessage);
            toastMessage.error(translate(`server_messages.${errorMessage}`));
            this.stopLoading();
        }
    };

    @action
    exportHtml = async (onSuccess: (key: string) => void): Promise<void> => {
        try {
            if (!this.validatePeriods()) {
                return;
            }

            this.startLoading("report_ticket_comparative.export_html");
            await this._exportHtmlReportTicketComparativeUseCase.call(
                {
                    startDate: formatDateTimeForReportApi(this.period[0]!),
                    endDate: formatDateTimeForReportApi(this.period[1]!),
                },
                {
                    compareStartDate: formatDateTimeForReportApi(this.comparePeriod[0]!),
                    compareEndDate: formatDateTimeForReportApi(this.comparePeriod[1]!),
                },
                this.buildEntityFilters()
            );
            onSuccess("report_ticket_comparative.success.export_html");
            this.stopLoading();
        } catch (err: any) {
            const errorMessage = err.message ?? "unknown";
            this.mapServerErrorToFields(errorMessage);
            toastMessage.error(translate(`server_messages.${errorMessage}`));
            this.stopLoading();
        }
    };
}
